/**
 * @file   src\form\BuyerListingCreateWantAdWeb.js
 * @brief  This file is responsible for buyerlisting create want ad
 * @date   May, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useNavigate, useParams } from "react-router-dom";
import WantAdLocation from "@/components/AgentWantAd/WantAdLocation";
import { useRef } from "react";
import {
  AGENT_WANTAD_KEY,
  IMG_JPEG_FORMAT,
  IS_LAND,
} from "@/constants/common";
import WantAdTiming from "@/components/AgentWantAd/WantAdTiming";
import WantAdType from "@/components/AgentWantAd/WantAdType";
import WantAdMustHaves from "@/components/AgentWantAd/WantAdMustHaves";
import WantAdLandMustHaves from "@/components/AgentWantAd/v2/WantAdLandMustHaves";
import NiceToHave from "@/components/Common/NiceToHave/NiceToHave";
import WantAdBudget from "@/components/AgentWantAd/WantAdBudget";
import WantAdPropertyPurchase from "@/components/AgentWantAd/WantAdPropertyPurchase";
import WantAdPurchaseFinance from "@/components/AgentWantAd/WantAdPurchaseFinance"
import WantAdDescription from "@/components/AgentWantAd/WantAdDescription";
import {
  convertToNumber,
  nameValidationBuyerListingForm,
} from "@/helpers/validations";
import { uploadMapImageToS3 } from "@/helpers/S3/uploadToS3";
import TextBox from "@/components/Common/TextBox";
import {
  createPreSignedUrl,
  getLocalStorage,
  toTitleCase,
} from "@/helpers/common";
import html2canvas from "html2canvas";
import { dataURLToBlob } from "@/helpers/createWantAd";
import { pageURLs } from "@/constants";
import { getAgentDetails } from "@/services/v2/agentClientsService";
import {
  saveBuyerListingWantAd,
  saveBuyerListStrengthenWantAd,
} from "@/services/v2/wantAdService";
import BuyerlistingHeader from "@/form/BuyerListingHeader";
import ErrorMessage from "@/components/Common/ErrorMessage";
import { PageWrapper } from "@/components/PageWrapper";
import useLoading from "@/hooks/useLoading";

const BuyerListingCreateWantAdForm = (props) => {

  const { id: slug } = useParams();

  const navigate = useNavigate();


  const { startLoading, stopLoading } = useLoading();

  const [locationCityError, setLocationCityError] = useState(false);
  const [locationRadiusError, setLocationRadiusError] = useState(false);
  const [timeLineError, setTimeLineError] = useState(false);
  const [propertyTypeError, setPropertyTypeError] = useState(false);
  const [requirementBedroomsError, setRequirementBedroomsError] = useState(false);
  const [requirementBathroomsError, setRequirementBathroomsError] = useState(false);

  const SAVE_LOADING_KEY = 'save-want-listing';

  const [requirementTextColor, setRequirementTextColor] = useState(false);
  const [budgetError, setBudgetError] = useState(false);
  const [selectedniceToHavesList, setSelectedNiceToHavesList] = useState([]);

  const [wantAdData, setWantAdData] = useState(
    JSON.parse(localStorage.getItem(AGENT_WANTAD_KEY)),
  ); // to set the want-ad data

  const [firstName, setFirstName] = useState(wantAdData?.clientFirstName || "");
  const [lastName, setLastName] = useState(wantAdData?.clientLastName || "");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [isValidId, setIsValidId] = useState(null);
  const YEAR_BUILT_MIN = 1900;
  const YEAR_BUILT_MAX = new Date().getFullYear();
  const [showUrlExpired, setShowUrlExpired] = useState(false);
  const [zipCodeError, setZipCodeError] = useState(false);
  const [locationError, setLocationError] = useState("");

  const [agentDetails, setAgentDetails] = useState(null);

  let error = [];

  const locationRef = useRef(null);
  const targetRef = useRef(null);
  const timeLineRef = useRef(null);
  const propertyTypeRef = useRef(null);
  const mustHaveRef = useRef(null);
  const BudgetRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);

  useEffect(() => {
    getWantAdDetails();
  }, [slug]);
  /**
   * To get want ad details
   */
  const getWantAdDetails = async () => {
    try {
      // const splitId = slug.split("-");
      // const agentID = splitId[splitId.length - 1];

      if (!slug) {
        setIsValidId(0);
        return;
      }

      const response = await getAgentDetails(slug);
      const responseResult = _.get(response, "result", null);

      // If no result data, treat as an error
      if (!responseResult?.data) {
        setIsValidId(0);
        return;
      }

      setAgentDetails(responseResult.data);

      if (responseResult.isReadyToReview) {
        if (responseResult.isPublished) {
          setShowUrlExpired(true);
        } else {
          navigate(pageURLs?.buyerListingConfirmation, {
            state: { agentDetails: responseResult.data },
          });
        }
      } else {
        setIsValidId(1);
      }
    } catch (err) {
      console.log("Error", err);
      setIsValidId(0);
    }
  };

  useEffect(() => {
    wantAdData?.nice2Have
      ? setSelectedNiceToHavesList(wantAdData?.nice2Have)
      : setSelectedNiceToHavesList([]);
    localStorage.setItem(AGENT_WANTAD_KEY, JSON.stringify(wantAdData));
    if (
      (wantAdData?.location && wantAdData?.county) ||
      wantAdData?.zipCode?.length > 0
    ) {
      setLocationCityError(false);
      setZipCodeError(false);
    }
    if (wantAdData?.mapRadius) {
      setLocationRadiusError(false);
    }

    if (wantAdData?.timeLine) {
      setTimeLineError(false);
    }

    if (wantAdData?.prprtyType != IS_LAND && wantAdData?.prprtyType) {
      if (wantAdData?.prptySubTypes && wantAdData?.prptySubTypes.length > 0) {
        setPropertyTypeError(false);
      }
    }

    if (wantAdData?.noBedRm) {
      setRequirementBedroomsError(false);
    }

    if (wantAdData?.noBathRm) {
      setRequirementBathroomsError(false);
    }

    if (wantAdData?.noBedRm && wantAdData?.noBathRm) {
      setRequirementTextColor(false);
    }
  }, [wantAdData]);

  /**
   * To handle map image capture
   * @param {*} type
   * @param {*} onkeyupSave
   */
  const handleCaptureMapImage = (type, onkeyupSave = false) => {
    const mapDiv = document.getElementById("mapboxId");
    if (targetRef.current) {
      html2canvas(mapDiv, {
        onclone: (clonedDocument) => {
          const buttonToHide =
            clonedDocument.getElementById("mapboxResetButton");
          if (buttonToHide) {
            buttonToHide.style.display = "none"; // Hide the button in the cloned DOM
          }
        },
      }).then(async (canvas) => {
        let dataURL = canvas.toDataURL(IMG_JPEG_FORMAT);
        const binaryData = dataURLToBlob(dataURL);
        await handleMapImageUpload(binaryData, type, onkeyupSave);
      });
    }
  };
  /**
   * To calculate percentage of completion
   * @returns
   */
  const calculatePercentageofCompletion = () => {
    let percentage = 0;
    let count = 0;
    if (wantAdData?.clientId) {
      count++;
    }
    if (wantAdData?.adType) {
      count++;
    }
    if (wantAdData?.timeLine) {
      count++;
    }
    if (wantAdData?.location && wantAdData?.location.trim() != "") {
      count++;
    }

    if (
      (wantAdData?.startDate && wantAdData?.endDate) ||
      (wantAdData?.month && wantAdData?.duration)
    ) {
      count++;
    }

    if (wantAdData?.prprtyType) {
      count++;
    }
    if (
      wantAdData?.noBathRm &&
      wantAdData?.noBedRm &&
      wantAdData?.sqFtMax &&
      wantAdData?.sqFtMin
    ) {
      count++;
    }
    if (wantAdData?.maxBdgt && wantAdData?.minBdgt) {
      count++;
    }
    if (wantAdData?.acreageMin && wantAdData?.acreageMin) {
      count++;
    }
    if (wantAdData?.adTitle) {
      count++;
    }

    percentage = count >= 8 ? 100 : 11 * count;
    return percentage;
  };
  /**
   * To upload map image in S3
   * @param {*} image
   * @param {*} type
   * @param {*} onkeyupSave
   */
  const handleMapImageUpload = async (image, type, onkeyupSave) => {
    const mapResponse = image;
    const preSignedUrlRes = mapResponse
      ? await createPreSignedUrl(true, _.get(wantAdData, "s3ImageFolder", ""))
      : alert("Something went wrong while fetching map image.");
    if (preSignedUrlRes && _.get(preSignedUrlRes, "errorCode") === 0) {
      const preSignedUrlsArray = _.get(preSignedUrlRes, "result", []);
      const preSignedS3Url = preSignedUrlsArray
        ? _.get(preSignedUrlsArray[0], "presignedUrl", "")
        : "";
      const s3FullUrl = await uploadMapImageToS3(preSignedS3Url, mapResponse);
      if (s3FullUrl) {
        const imageUrl = s3FullUrl ? s3FullUrl.split("?")[0] : "";
        await saveMemberWantAdData(imageUrl, onkeyupSave, type);
      }
    }
  };
  /**
   * To save want ad data
   * @param {*} imageUrl
   * @param {*} onkeyupSave
   * @param {*} type
   */
  const saveMemberWantAdData = async (imageUrl, onkeyupSave = false, type) => {
    try {
      let requestPayLoad = JSON.parse(localStorage.getItem(AGENT_WANTAD_KEY));
      requestPayLoad.agentId = agentDetails.UserId;
      requestPayLoad.adType = "BUY";

      if (!requestPayLoad) {
        requestPayLoad = JSON.parse(localStorage.getItem(AGENT_WANTAD_KEY));
      }

      requestPayLoad.mapImg = imageUrl;
      requestPayLoad.cmpldStep = _.get(wantAdData, "actvStep", 1);
      requestPayLoad.IsSavedDraft = true;
      requestPayLoad.IsPublished = false;
      requestPayLoad.cmpldPerctg = calculatePercentageofCompletion();

      if (type === "save") {
        requestPayLoad.isReadyToReview = true;
      }

      let arrayValue = [];

      if (requestPayLoad?.zipIdValues) {
        requestPayLoad?.zipIdValues.map((zip) => {
          arrayValue.push(zip?.zipId);
        });
        requestPayLoad.zipId = arrayValue;
      } else if (requestPayLoad?.zipCode) {
        requestPayLoad?.zipCode.map((zip) => {
          if (zip?.zipId && zip?.zipId !== null) {
            arrayValue.push(zip?.zipId);
          }
        });
        requestPayLoad.zipId = arrayValue;
      }

      requestPayLoad.isZipcodeBased =
        requestPayLoad.zipId?.length > 0 ? true : false;

      const response = await saveBuyerListingWantAd(requestPayLoad);
      const responseResult = _.get(response, "result", null);

      if (response.errorCode === 0 && responseResult) {
        if (
          wantAdData.purchaseType ||
          wantAdData.prchTypeId ||
          wantAdData.pFinance
        ) {
          await saveStrengthenData(responseResult.wantAdId, onkeyupSave, type);
        } else {
          if (type === "save") {
            localStorage.removeItem(AGENT_WANTAD_KEY);
            navigate(pageURLs?.buyerListingConfirmation, {
              state: { agentDetails: agentDetails },
            });
          } else {
            handleFormValues({
              wantAdId: responseResult.wantAdId,
            });
          }
        }
      } else {
        console.log("Something went wrong");
        setIsValidId(0);
      }
    } catch (err) {
      console.log("Error", err);
      setIsValidId(0);
    }
  };
  /**
   * To save strengthen want ad data
   * @param {*} wantAdIdValue
   * @param {*} onkeyupSave
   * @param {*} type
   */
  const saveStrengthenData = async (wantAdIdValue, onkeyupSave, type) => {
    const wantAd = getLocalStorage(AGENT_WANTAD_KEY);
    const wantAdId = wantAdIdValue;
    let requestPayLoad = {};
    let isApproveLoan = "";
    if (
      _.get(wantAdData, "isPreApprLoan", null) === "Yes" ||
      _.get(wantAdData, "isPreApprLoan", null) == true
    ) {
      isApproveLoan = true;
    } else if (
      _.get(wantAdData, "isPreApprLoan", null) === "No" ||
      _.get(wantAdData, "isPreApprLoan", null) == true
    ) {
      isApproveLoan = false;
    }
    requestPayLoad.isPreApprLoan = isApproveLoan;
    const is1031Exchange = wantAd?.is1031Exchange === "Yes" ? true : false;
    requestPayLoad.wantAdId = wantAdId;
    requestPayLoad.isPreApprLoan = isApproveLoan;
    requestPayLoad.is1031Exchange = is1031Exchange;
    requestPayLoad.completedStep = 1;
    requestPayLoad.adType = "BUY_STRENGTHEN";
    requestPayLoad.maxBdgt = wantAdData.maxBdgt;
    requestPayLoad.minBdgt = wantAdData.minBdgt;
    requestPayLoad.pFinance = wantAdData.pFinance || wantAdData.finTypeId;
    requestPayLoad.purchaseType =
      wantAdData.purchaseType || wantAdData.prchTypeId;

    saveBuyerListStrengthenWantAd(requestPayLoad)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          if (type == "save") {
            localStorage.removeItem(AGENT_WANTAD_KEY);
            navigate(pageURLs?.buyerListingConfirmation, {
              state: { agentDetails: agentDetails },
            });
          } else {
            handleFormValues({ client_Want_ad_Id: wantAdId });
          }
        }
      })
      .catch((err) => {
        console.log("Error", err);
        setIsValidId(0);
      });
  };

  const handleFormValues = (stateObject) => {
    Object.keys(stateObject).forEach(function (key) {
      setWantAdData((prevState) => ({
        ...prevState,
        [key]: stateObject[key],
      }));
    });
  };

  const handleFirstName = (name) => {
    setFirstNameError("");
    setFirstName(name);
    if (name.trim() === "") {
      setFirstNameError("Enter First Name");
      error.push("firstNameRef", "firstName");
    } else if (!nameValidationBuyerListingForm(name.trim())) {
      setFirstNameError("Invalid input");
      error.push("firstNameRef", "firstName");
    }
    handleFormValues({ clientFirstName: name });
  };

  const handleLastName = (name) => {
    setLastNameError("");
    setLastName(name);
    if (name.trim() === "") {
      setLastNameError("Enter Last Name");
      error.push("lastNameRef", "lastName");
    } else if (!nameValidationBuyerListingForm(name.trim())) {
      setLastNameError("Invalid input");
      error.push("lastNameRef", "lastName");
    }
    handleFormValues({ clientLastName: name });
  };

  const clearPreviousStates = (params) => {
    if (params) {
      const { type } = params;
      if (type === "clearAll") {
        setWantAdData({
          actvStep: 1,
          cmpldPerctg: 2,
          lat: wantAdData?.lat,
          lng: wantAdData?.lng,
          isPublished: wantAdData?.isPublished,
          client_Want_ad_Id: wantAdData?.client_Want_ad_Id,
          isFilledByClient: wantAdData?.isFilledByClient,
          clientFirstName: wantAdData?.clientFirstName,
          clientLastName: wantAdData?.clientLastName,
          clientEmail: wantAdData?.clientEmail,
          clientPhone: wantAdData?.clientPhone,
          isAgentCreated: wantAdData?.isAgentCreated,
          wantAdId: wantAdData?.wantAdId,
        });
      } else if (type === "clearFlexDates") {
        const array = { ...wantAdData }; // make a separate copy of the state
        ["duration", "month"].forEach((e) => delete array[e]);
        setWantAdData(array);
      } else if (type === "dateRange") {
        const array = { ...wantAdData }; // make a separate copy of the state
        ["startDate", "endDate"].forEach((e) => delete array[e]);
        setWantAdData(array);
      } else if (type === "moveInTypeChangeLTR") {
        const array = { ...wantAdData }; // make a separate copy of the state
        ["startDate"].forEach((e) => delete array[e]);
        setWantAdData(array);
      } else if (type == "clearAllExceptClient") {
        setWantAdData({
          actvStep: 10,
          cmpldPerctg: 2,
          clientId: wantAdData?.clientId,
          isAgentAsSeller: wantAdData?.isAgentAsSeller,
          wantAdId: wantAdData?.wantAdId,
          s3ImageFolder: wantAdData?.s3ImageFolder,
          userId: wantAdData?.userId,
          lat: wantAdData?.lat,
          lng: wantAdData?.lng,
          isPublished: wantAdData?.isPublished,
          isEdit: wantAdData?.isEdit,
          client_Want_ad_Id: wantAdData?.client_Want_ad_Id,
          isFilledByClient: wantAdData?.isFilledByClient,
          clientFirstName: wantAdData?.clientFirstName,
          clientLastName: wantAdData?.clientLastName,
          clientEmail: wantAdData?.clientEmail,
          clientPhone: wantAdData?.clientPhone,
          isAgentCreated: wantAdData?.isAgentCreated,
        });
        setLocationCityError(false);
        setLocationRadiusError(false);
        setPropertyTypeError(false);
        setRequirementBathroomsError(false);
        setRequirementBedroomsError(false);
        setBudgetError(false);
        setTimeLineError(false);
        setZipCodeError(false);
      } else if (type == "clearErrorStateOnPropertySelection") {
        // clear previous state values if user changes property type (eg from lot-land to other types and vice versa)
        const array = { ...wantAdData }; // make a separate copy of the state
        [
          "nice2Have",
          "noBedRm",
          "noBathRm",
          "noOfBeds",
          "sqFtMin",
          "sqFtMax",
          "grgSpce",
          "minLtSz",
          "maxLtSz",
          "yrBltMin",
          "yrBltMax",
          "isNwCnstctn",
          "acreageMin",
          "acreageMax",
          "lotType",
          "lotTypeText",
          "roadType",
          "rdTypText",
          "isPrvtDrInst",
          "onSiteElec",
          "waterSupply",
          "wSText",
          "isDrldWelInst",
          "wasteWtrSys",
          "wWSysText",
          "isSepTankInst",
          "isSepTankAppr",
          "prptySubTypes",
          "adaAccess",
          "accsPvtBthRm",
          "alwPets",
          "alwCats",
          "alwLrgDogs",
          "alwSmlDogs",
          "prptySubTypes",
          "adTitle",
          "adDesc",
          "minBdgt",
          "maxBdgt",
          "stories",
          "yearBuiltOption",
          "mnLtSzId",
          "mnLtSzTxt",
          "mxLtSzId",
          "mxLtSzTxt",
        ].forEach((e) => delete array[e]);

        setWantAdData(array);

        setRequirementBathroomsError(false);
        setRequirementBedroomsError(false);
        setBudgetError(false);
        setTimeLineError(false);
      }
    }
  };


  async function handleSave() {

    startLoading(SAVE_LOADING_KEY);

    handleFirstName(firstName);
    handleLastName(lastName);

    if (
      (!wantAdData?.location || !wantAdData?.county) &&
      (!wantAdData?.zipCode || wantAdData?.zipCode.length == 0)
    ) {
      setLocationCityError(true);
      setZipCodeError(true);
      error.push("locationRef", "city");
    } else {
      if (
        wantAdData?.location ||
        wantAdData?.location != "" ||
        !wantAdData?.zipCode.length > 0
      ) {
        if (!wantAdData?.mapRadius) {
          setLocationRadiusError(true);
          error.push("locationRef", "mapRadius");
        }
      }
    }

    if (!wantAdData?.timeLine) {
      setTimeLineError(true);
      error.push("timeLineRef", "timeLine");
    }

    if (!wantAdData?.prprtyType) {
      setPropertyTypeError(true);
      error.push("propertyTypeRef", "prprtyType");
    } else {
      if (
        (wantAdData?.prprtyType != IS_LAND && !wantAdData?.prptySubTypes) ||
        wantAdData?.prptySubTypes?.length == 0
      ) {
        setPropertyTypeError(true);
        error.push("propertyTypeRef", "prprtyType");
      }
    }

    if (wantAdData?.prprtyType != IS_LAND) {
      if (!wantAdData?.noBedRm) {
        setRequirementBedroomsError(true);
        error.push("mustHaveRef", "noBedRm");
      }
      if (!wantAdData?.noBathRm) {
        setRequirementBathroomsError(true);
        error.push("mustHaveRef", "noBathRm");
      }
      if (!wantAdData?.sqFtMin && !wantAdData?.sqFtMax) {
        setBudgetError(true);
        error.push("mustHaveRef", "noSqftSelected");
      }
      if (!wantAdData?.noBedRm || !wantAdData?.noBathRm) {
        setRequirementTextColor(true);
      }
    }

    if (wantAdData?.sqFtMax && wantAdData?.sqFtMin) {
      if (
        convertToNumber(wantAdData?.sqFtMin) >=
        convertToNumber(wantAdData?.sqFtMax)
      ) {
        error.push("mustHaveRef", "sqftMaxLesser");
        setRequirementTextColor(true);
      }
    }

    if (
      wantAdData?.prprtyType == IS_LAND &&
      (!wantAdData?.minLtSz || !wantAdData?.maxLtSz)
    ) {
      error.push("mustHaveRef", "noLotSelected");
      setRequirementTextColor(true);
    }

    if (wantAdData?.minLtSz && wantAdData?.maxLtSz) {
      if (
        convertToNumber(wantAdData?.minLtSz) >=
        convertToNumber(wantAdData?.maxLtSz)
      ) {
        error.push("mustHaveRef", "lotSizeMaxLesser");
        setRequirementTextColor(true);
      }
    }

    if (wantAdData?.yearBuiltOption == 1) {
      if (wantAdData.yrBltMin && wantAdData.yrBltMax) {
        if (
          convertToNumber(wantAdData.yrBltMax) < YEAR_BUILT_MIN ||
          convertToNumber(wantAdData.yrBltMax) > YEAR_BUILT_MAX ||
          convertToNumber(wantAdData.yrBltMin) < YEAR_BUILT_MIN ||
          convertToNumber(wantAdData.yrBltMin) > YEAR_BUILT_MAX
        ) {
          (convertToNumber(wantAdData.yrBltMax) < YEAR_BUILT_MIN ||
            convertToNumber(wantAdData.yrBltMax) > YEAR_BUILT_MAX) &&
            error.push("yearBuiltMaxRange");
          (convertToNumber(wantAdData.yrBltMin) < YEAR_BUILT_MIN ||
            convertToNumber(wantAdData.yrBltMin) > YEAR_BUILT_MAX) &&
            error.push("yearBuiltMinRange");
        } else if (
          convertToNumber(wantAdData.yrBltMax) <
          convertToNumber(wantAdData.yrBltMin)
        ) {
          error.push("mustHaveRef", "yearBuiltError");
        } else if (
          convertToNumber(wantAdData.yrBltMin) <
          convertToNumber(wantAdData.yrBltMin)
        ) {
          error.push("mustHaveRef", "yearBuiltError");
        }
      } else if (!wantAdData.yrBltMin || !wantAdData.yrBltMax) {
        !wantAdData.yrBltMin && error.push("mustHaveRef", "yearBuiltMinDate");
        !wantAdData.yrBltMax && error.push("mustHaveRef", "yearBuiltMaxDate");
      }
    }

    if (!wantAdData?.minBdgt && !wantAdData?.maxBdgt) {
      error.push("BudgetRef", "noBudget");
      setBudgetError(true);
    }

    if (wantAdData?.minBdgt && wantAdData?.maxBdgt) {
      if (
        convertToNumber(wantAdData?.minBdgt) >=
        convertToNumber(wantAdData?.maxBdgt)
      ) {
        error.push("BudgetRef", "maxBdgt");
        setBudgetError(true);
      }
    }

    if (error.length == 0) {
      handleFormValues({ validationErrors: [], updatedAt: Date.now() });

      await new Promise(resolve => setTimeout(resolve, 2000));
      await handleCaptureMapImage("save", true);

      stopLoading(SAVE_LOADING_KEY);

    } else {
      if (error[0] == "firstNameRef") {
        window.scrollTo(0, firstNameRef.current.offsetTop);
      } else if (error[0] == "lastNameRef") {
        window.scrollTo(0, lastNameRef.current.offsetTop);
      } else if (error[0] == "locationRef") {
        window.scrollTo(0, locationRef.current.offsetTop);
      } else if (error[0] == "timeLineRef") {
        window.scrollTo(0, timeLineRef.current.offsetTop);
      } else if (error[0] == "propertyTypeRef") {
        window.scrollTo(0, propertyTypeRef.current.offsetTop);
      } else if (error[0] == "mustHaveRef") {
        window.scrollTo(0, mustHaveRef.current.offsetTop);
      } else if (error[0] == "BudgetRef") {
        window.scrollTo(0, BudgetRef.current.offsetTop);
      }
      handleFormValues({ validationErrors: error, updatedAt: Date.now() });

      stopLoading(SAVE_LOADING_KEY);
    }

  }

  const isLotLandPropertyType = wantAdData?.prprtyType === IS_LAND;

  const disabledSubmitButton =
    wantAdData?.invalidLocation ||
    !wantAdData?.prprtyType ||
    !wantAdData?.timeLine ||
    !firstName ||
    !lastName ||
    !wantAdData?.minBdgt ||
    !wantAdData?.maxBdgt ||
    (isLotLandPropertyType && (!wantAdData?.minLtSz || !wantAdData?.maxLtSz)) ||
    (!isLotLandPropertyType && (!wantAdData?.noBedRm || !wantAdData?.noBathRm || !wantAdData?.sqFtMin || !wantAdData?.sqFtMax))

  if (showUrlExpired) {
    return (
      <ErrorMessage
        message="The URL cannot be accessed as the link has expired."
        formValues={wantAdData}
      />
    )
  }

  if (isValidId === 0) {
    return (
      <ErrorMessage
        message="The url is not valid or Server not responding!Please Try again in sometime!"
        wantAdData={wantAdData}
      />
    )
  }

  return (
    <PageWrapper renderGitchaDefaultHeader={<BuyerlistingHeader agentDetails={agentDetails} />}>

      <Row className="pb-4">
        <Col md={12}>
          <div className="edit-want-ad-wrap mw-626">
            <h2>
              Let {toTitleCase(agentDetails?.FirstName)} know what you’re
              looking to buy:
            </h2>
            <h6 className="mb-40">
              {" "}
              The more details you provide, the better!
            </h6>

            <div className="">
              <div className="center-content-sec mw-100 mt-0 mb-48">
                <div
                  className={
                    locationRadiusError
                      ? "wantad-location-wrap select-type-wrap red-marked-wrap"
                      : ""
                  }
                  ref={locationRef}
                >
                  <h6
                    className={`${locationCityError || locationRadiusError ? "red-marked-heading" : ""}`}
                  >
                    Location{" "}
                  </h6>
                  <WantAdLocation
                    handleFormValues={handleFormValues}
                    location={_.get(wantAdData, "location", null)}
                    s3Imagefolder={_.get(
                      wantAdData,
                      "s3Imagefolder",
                      false,
                    )}
                    lat={_.get(wantAdData, "lat", null)}
                    lng={_.get(wantAdData, "lng", null)}
                    mapZoom={_.get(wantAdData, "mapZoom", null)}
                    mapRadius={_.get(wantAdData, "mapRadius", null)}
                    neighborHood={_.get(wantAdData, "neighborHood", null)}
                    highSchool={_.get(wantAdData, "highSchool", null)}
                    zip={_.get(wantAdData, "zip", null)}
                    adType={_.get(wantAdData, "adType", null)}
                    city={_.get(wantAdData, "city", null)}
                    state={_.get(wantAdData, "state", null)}
                    county={_.get(wantAdData, "county", null)}
                    session={AGENT_WANTAD_KEY}
                    targetRef={targetRef}
                    isGitchaAvailable={_.get(
                      wantAdData,
                      "isGitchaAvailable",
                      null,
                    )}
                    locationCityError={locationCityError}
                    locationRadiusError={locationRadiusError}
                    isFromBuyersListingService={true}
                    client_Want_ad_Id={_.get(
                      wantAdData,
                      "client_Want_ad_Id",
                      null,
                    )}
                    zipCode={_.get(wantAdData, "zipCode", null)}
                    zipIdValues={_.get(wantAdData, "zipIdValues", null)}
                    zipCodeError={zipCodeError}
                    setLocationCityError={setLocationCityError}
                    setLocationError={setLocationError}
                    locationError={locationError}
                    mapContainerStyle={{ height: '450px', paddingBottom: 0, borderRadius: 14 }}
                  />
                </div>


                <div
                  className="wantad-type-wrap select-type-wrap red-marked-wrap"
                  ref={timeLineRef}
                >
                  <h6 className={timeLineError ? "red-marked-heading" : ""}>
                    Timeline <span>*</span>
                  </h6>
                  <WantAdTiming
                    handleFormValues={handleFormValues}
                    timeLine={_.get(wantAdData, "timeLine", null)}
                    adType={_.get(wantAdData, "adType", null)}
                    timeLineError={timeLineError}
                  />
                </div>

                <div
                  className={
                    propertyTypeError
                      ? "wantad-type-wrap select-type-wrap red-marked-wrap"
                      : "wantad-type-wrap select-type-wrap"
                  }
                  ref={propertyTypeRef}
                >
                  <WantAdType
                    handleFormValues={handleFormValues}
                    propertyCategory={_.get(wantAdData, "propertyCategory", null)}
                    clearPreviousStates={clearPreviousStates}
                    propertyTypeError={propertyTypeError}
                    prptySubTypes={_.get(wantAdData, "prptySubTypes", [])}
                  />
                </div>

                {wantAdData?.prprtyType && (
                  <div
                    className={
                      requirementTextColor
                        ? "requirements-wrap red-marked-wrap mb-3"
                        : "requirements-wrap mb-3"
                    }
                    ref={mustHaveRef}
                  >
                    <h6>Requirements</h6>

                    {wantAdData?.prprtyType != IS_LAND ? (
                      <WantAdMustHaves
                        handleFormValues={handleFormValues}
                        noOfBedrooms={_.get(wantAdData, "noBedRm", null)}
                        noOfBathrooms={_.get(wantAdData, "noBathRm", null)}
                        noOfHalfBathrooms={_.get(wantAdData, "noHalfBathRm", null)}
                        SFMin={_.get(wantAdData, "sqFtMin", null)}
                        SFMax={_.get(wantAdData, "sqFtMax", null)}
                        garageSpace={_.get(wantAdData, "grgSpce", null)}
                        yearBuiltMin={_.get(wantAdData, "yrBltMin", null)}
                        yearBuildMax={_.get(wantAdData, "yrBltMax", null)}
                        mustHaveErrors={_.get(
                          wantAdData,
                          "validationErrors",
                          null,
                        )}
                        stories={_.get(wantAdData, "stories", null)}
                        yearBuiltOption={_.get(
                          wantAdData,
                          "yearBuiltOption",
                          null,
                        )}
                        minLtSz={_.get(wantAdData, "minLtSz", null)}
                        maxLtSz={_.get(wantAdData, "maxLtSz", null)}
                        adaAccess={_.get(wantAdData, "adaAccess", null)}
                        requirementBedroomsError={requirementBedroomsError}
                        requirementBathroomsError={
                          requirementBathroomsError
                        }
                        propertyType={wantAdData?.prprtyType}
                      />
                    ) : (
                      <WantAdLandMustHaves
                        handleFormValues={handleFormValues}
                        lotType={_.get(wantAdData, "lotType", null)}
                        roadType={_.get(wantAdData, "roadType", null)}
                        isPrivtDrilInstld={_.get(
                          wantAdData,
                          "isPrvtDrInst",
                          null,
                        )}
                        onSiteElec={_.get(wantAdData, "onSiteElec", null)}
                        waterSupply={_.get(wantAdData, "waterSupply", null)}
                        isDrldWelInstld={_.get(
                          wantAdData,
                          "isDrldWelInst",
                          null,
                        )}
                        wasteWtrSys={_.get(wantAdData, "wasteWtrSys", null)}
                        isSepTankInstld={_.get(
                          wantAdData,
                          "isSepTankInst",
                          null,
                        )}
                        isSepTankApprvd={_.get(
                          wantAdData,
                          "isSepTankAppr",
                          null,
                        )}
                        minLtSz={_.get(wantAdData, "minLtSz", null)}
                        maxLtSz={_.get(wantAdData, "maxLtSz", null)}
                        mustHaveErrors={_.get(
                          wantAdData,
                          "validationErrors",
                          null,
                        )}
                        propertyType={wantAdData?.prprtyType}
                      />
                    )}
                  </div>
                )}

                {/* ------------------------------------------Nice-to-haves------------------------------------------ */}
                {wantAdData?.prprtyType && (
                  <NiceToHave
                    selectedNiceToHave={wantAdData?.nice2Have}
                    // niceToHavesList={niceToHavesList}
                    selectedniceToHavesList={selectedniceToHavesList}
                    propertyType={wantAdData?.prprtyType}
                    adType={wantAdData?.adType}
                    handleFormValues={handleFormValues}
                    title="Nice-to-haves"
                  />
                )}

                {/* ------------------------------------------Finance/Purchase type ------------------------------------------ */}
                {wantAdData?.prprtyType && (
                  <div
                    className={
                      budgetError
                        ? "finance-wrap red-marked-wrap pb-24"
                        : "finance-wrap pb-24"
                    }
                    ref={BudgetRef}
                  >
                    {/* <h6 className={budgetError && "red-marked-heading"}>Finance</h6> */}
                    <h6>Finance</h6>
                    <WantAdBudget
                      handleFormValues={handleFormValues}
                      budgetMin={_.get(wantAdData, "minBdgt", null)}
                      budgetMax={_.get(wantAdData, "maxBdgt", null)}
                      isAdjustBdget={_.get(
                        wantAdData,
                        "isAdjustBdget",
                        null,
                      )}
                      isPreApprLoan={_.get(
                        wantAdData,
                        "isPreApprLoan",
                        null,
                      )}
                      adType={wantAdData?.adType}
                      errors={_.get(wantAdData, "validationErrors", null)}
                    />

                    <div className="wantad-type-wrap select-type-wrap pb-2">
                      <>
                        <label className="form-label mb-12">
                          Purchase Type
                        </label>
                        <WantAdPropertyPurchase
                          handleFormValues={handleFormValues}
                          propertyPurchase={
                            _.get(wantAdData, "prchTypeId", null) ||
                            _.get(wantAdData, "purchaseType", null)
                          }
                          is1031Exchange={_.get(
                            wantAdData,
                            "is1031Exchange",
                            null,
                          )}
                          isEdit="true"
                        />
                      </>

                      <>
                        <label className="form-label">Financing</label>
                        <WantAdPurchaseFinance
                          handleFormValues={handleFormValues}
                          timeLine={_.get(wantAdData, "timeLine", null)}
                          isFromBuyersListingService={true}
                          financeType={
                            _.get(wantAdData, "finTypeId", null) ||
                            _.get(wantAdData, "pFinance", null)
                          }
                          isPreApprLoan={_.get(
                            wantAdData,
                            "isPreApprLoan",
                            null,
                          )}
                          agreePreApprovedLoan={_.get(
                            wantAdData,
                            "agreePreApprovedLoan",
                            null,
                          )}
                          errors={_.get(
                            wantAdData,
                            "validationErrors",
                            null,
                          )}
                        />
                      </>
                      {/* } */}
                    </div>
                  </div>
                )}
                {/* ------------------------------------------/Finance/Purchase type------------------------------------------ */}

                {/* ------------------------------------------Description------------------------------------------ */}
                <div className={"finance-wrap pb-24"}>
                  <WantAdDescription
                    handleFormValues={handleFormValues}
                    adDescription={_.get(wantAdData, "adDesc", "")}
                    isFromBuyersListingService={true}
                    errors={[]}
                    descriptionMaxLength={500}
                  />
                </div>

                {/* [US-008] Your Name - moved to bottom of form */}
                <div className="wantad-location-wrap mb-3">
                  <h6>Your Name</h6>
                  <Row>
                    <Col md={6}>
                      <div
                        className="common-input-wrap mb-2"
                        ref={firstNameRef}
                      >
                        <label className="form-label">
                          First Name<span>*</span>
                        </label>
                        <TextBox
                          id="fName"
                          name="firstName"
                          type="text"
                          maxLength={30}
                          value={toTitleCase(firstName)}
                          placeholder="First name"
                          className="form-control"
                          onChange={(e) => handleFirstName(e.target.value)}
                          errorClass="error-validation-msg"
                          errorMessage={firstNameError}
                        />
                      </div>
                    </Col>
                    <Col md={6}>
                      <div
                        className="common-input-wrap mb-2"
                        ref={lastNameRef}
                      >
                        <label className="form-label">
                          Last Name<span>*</span>
                        </label>
                        <TextBox
                          id="lName"
                          name="lastName"
                          maxLength={30}
                          value={toTitleCase(lastName)}
                          type="text"
                          placeholder="Last name"
                          className="form-control"
                          errorClass="error-validation-msg"
                          onChange={(e) => handleLastName(e.target.value)}
                          errorMessage={lastNameError}
                        />
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
            </div>

            <div className="edit-want-ad-btnwrap  edit-want-ad-btnwrap01 mw-626 mt-4">
              <button
                type="button"
                className="btn btn btn-primary"
                disabled={disabledSubmitButton}
                onClick={handleSave}
              >
                Send to my agent
              </button>
            </div>
          </div>
        </Col>
      </Row>

    </PageWrapper>
  );
};

export default BuyerListingCreateWantAdForm;
